import { useState } from 'react'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'

interface TerminateLeaseDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  contractNumber: string
  onConfirm: (params: { termination_reason: string; termination_date: string }) => void
  isPending: boolean
}

export function TerminateLeaseDialog({
  open,
  onOpenChange,
  contractNumber,
  onConfirm,
  isPending,
}: TerminateLeaseDialogProps) {
  const [reason, setReason] = useState('')
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0])

  function handleConfirm() {
    if (!reason.trim() || !date) return
    onConfirm({ termination_reason: reason.trim(), termination_date: date })
  }

  function handleOpenChange(next: boolean) {
    if (!next) {
      setReason('')
      setDate(new Date().toISOString().split('T')[0])
    }
    onOpenChange(next)
  }

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Terminate Lease</AlertDialogTitle>
          <AlertDialogDescription>
            Terminate lease{' '}
            <span className="font-mono font-semibold">{contractNumber}</span>.
            If the aircraft is still on the ramp, it will remain visible with a
            warning badge until departure.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="term-reason">Reason *</Label>
            <Textarea
              id="term-reason"
              placeholder="e.g. Non-payment, early exit, eviction…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="term-date">Termination Date *</Label>
            <Input
              id="term-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isPending || !reason.trim() || !date}
          >
            {isPending ? 'Terminating…' : 'Terminate Lease'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
